import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import { AdminQueueState } from './ui/AdminQueueState';
import { useToast } from '../contexts/ToastContext';
import {
  adminGetFactorySpecs,
  adminSaveFactorySpec,
  adminDeleteFactorySpec,
} from '../services/api/adminExpansion';
import type { AdminFactorySpec, FactorySpecInput } from '../services/api/adminExpansion';

interface SpecForm {
  year: string;
  make: string;
  model: string;
  trim: string;
  power_hp: string;
  torque_lbft: string;
  weight_lbs: string;
  engine: string;
  transmission: string;
  drivetrain: string;
  forced_induction: string;
  source: string;
  is_estimated: boolean;
}

const emptySpecForm: SpecForm = {
  year: '',
  make: '',
  model: '',
  trim: '',
  power_hp: '',
  torque_lbft: '',
  weight_lbs: '',
  engine: '',
  transmission: '',
  drivetrain: '',
  forced_induction: '',
  source: '',
  is_estimated: false,
};

/** Admin catalog for factory vehicle specs used in build comparisons. */
export const AdminFactorySpecs: React.FC = () => {
  const { showToast } = useToast();
  const [specs, setSpecs] = useState<AdminFactorySpec[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [specMake, setSpecMake] = useState('');
  const [specModel, setSpecModel] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AdminFactorySpec | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [specEditorOpen, setSpecEditorOpen] = useState(false);
  const [specForm, setSpecForm] = useState<SpecForm>(emptySpecForm);
  const [specEditingId, setSpecEditingId] = useState<number | null>(null);
  const [specFormError, setSpecFormError] = useState<string | null>(null);
  const [savingSpec, setSavingSpec] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSpecs(await adminGetFactorySpecs(specMake.trim() || undefined, specModel.trim() || undefined));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load factory specs');
    } finally {
      setLoading(false);
    }
  }, [specMake, specModel]);

  useEffect(() => {
    void load();
  }, [load]);

  const setSpecField = <K extends keyof SpecForm>(key: K, value: SpecForm[K]) => {
    setSpecForm((prev) => ({ ...prev, [key]: value }));
  };

  const openCreateSpec = () => {
    setSpecEditingId(null);
    setSpecForm(emptySpecForm);
    setSpecFormError(null);
    setSpecEditorOpen(true);
  };

  const openEditSpec = (spec: AdminFactorySpec) => {
    setSpecEditingId(spec.id);
    setSpecForm({
      year: String(spec.year),
      make: spec.make,
      model: spec.model,
      trim: spec.trim,
      power_hp: spec.power_hp !== null ? String(spec.power_hp) : '',
      torque_lbft: spec.torque_lbft !== null ? String(spec.torque_lbft) : '',
      weight_lbs: spec.weight_lbs !== null ? String(spec.weight_lbs) : '',
      engine: spec.engine ?? '',
      transmission: spec.transmission ?? '',
      drivetrain: spec.drivetrain ?? '',
      forced_induction: spec.forced_induction ?? '',
      source: spec.source ?? '',
      is_estimated: spec.is_estimated,
    });
    setSpecFormError(null);
    setSpecEditorOpen(true);
  };

  const saveSpec = async (e: React.FormEvent) => {
    e.preventDefault();
    setSpecFormError(null);
    const year = Number(specForm.year);
    if (!Number.isInteger(year) || year < 1900 || year > 2100) {
      setSpecFormError('Year must be between 1900 and 2100.');
      return;
    }
    if (!specForm.make.trim() || !specForm.model.trim()) {
      setSpecFormError('Make and model are required.');
      return;
    }
    const payload: FactorySpecInput = {
      year,
      make: specForm.make.trim(),
      model: specForm.model.trim(),
      trim: specForm.trim.trim(),
      power_hp: specForm.power_hp.trim() || null,
      torque_lbft: specForm.torque_lbft.trim() || null,
      weight_lbs: specForm.weight_lbs.trim() || null,
      engine: specForm.engine.trim() || null,
      transmission: specForm.transmission.trim() || null,
      drivetrain: specForm.drivetrain.trim() || null,
      forced_induction: specForm.forced_induction.trim() || null,
      source: specForm.source.trim() || null,
      is_estimated: specForm.is_estimated,
    };
    setSavingSpec(true);
    try {
      const result = await adminSaveFactorySpec(payload);
      showToast(result.created ? 'Factory spec created.' : 'Factory spec updated.', 'success');
      setSpecEditorOpen(false);
      await load();
    } catch (err: unknown) {
      setSpecFormError(err instanceof Error ? err.message : 'Failed to save factory spec');
    } finally {
      setSavingSpec(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminDeleteFactorySpec(deleteTarget.id);
      showToast('Factory spec deleted.', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Delete failed', 'error');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        eyebrow="Admin"
        title="Factory specs"
        description="Catalog factory power, weight, and drivetrain data for build comparisons."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
        className="flex flex-col sm:flex-row gap-2 sm:items-end"
      >
        <div className="flex-1">
          <label htmlFor="spec-filter-make" className="input-label">
            Make
          </label>
          <input
            id="spec-filter-make"
            type="text"
            value={specMake}
            onChange={(e) => setSpecMake(e.target.value)}
            placeholder="Filter by make"
            className="input"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="spec-filter-model" className="input-label">
            Model
          </label>
          <input
            id="spec-filter-model"
            type="text"
            value={specModel}
            onChange={(e) => setSpecModel(e.target.value)}
            placeholder="Filter by model"
            className="input"
          />
        </div>
        <div className="flex gap-2">
          <Button type="submit" variant="secondary">
            Search
          </Button>
          <Button type="button" variant="primary" onClick={openCreateSpec}>
            Add spec
          </Button>
        </div>
      </form>

      {error ? (
        <AdminQueueState
          variant="error"
          title="Could not load factory specs"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}

      {loading ? (
        <p className="text-gray-400 text-sm" role="status">
          Loading…
        </p>
      ) : !error && specs.length === 0 ? (
        <AdminQueueState
          variant={specMake || specModel ? 'filter-empty' : 'empty'}
          title={specMake || specModel ? 'No factory specs match your filters' : 'No factory specs in the catalog'}
          description={
            specMake || specModel
              ? 'Try different make or model filters, or add a new spec.'
              : 'Add factory specs to power comparison data on build profiles.'
          }
          onClearFilter={
            specMake || specModel
              ? () => {
                  setSpecMake('');
                  setSpecModel('');
                }
              : undefined
          }
        />
      ) : !loading && specs.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
          <table className="min-w-full text-sm">
            <caption className="sr-only">Factory vehicle spec catalog entries</caption>
            <thead className="bg-[#141414]/80 text-gray-400 text-left">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Vehicle
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Power
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Torque
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Weight
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Engine
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Drivetrain
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Estimated
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {specs.map((s) => (
                <tr key={s.id}>
                  <td className="px-4 py-3">
                    <p className="text-white whitespace-nowrap">
                      {s.year} {s.make} {s.model}
                    </p>
                    {s.trim ? <p className="text-gray-500 text-xs">{s.trim}</p> : null}
                  </td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                    {s.power_hp !== null ? `${s.power_hp} hp` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                    {s.torque_lbft !== null ? `${s.torque_lbft} lb-ft` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums whitespace-nowrap">
                    {s.weight_lbs !== null ? `${s.weight_lbs} lbs` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {s.engine ?? '—'}
                    {s.forced_induction ? <span className="block">{s.forced_induction}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {[s.drivetrain, s.transmission].filter(Boolean).join(' / ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {s.is_estimated ? 'Estimated' : 'Confirmed'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <Button type="button" variant="ghost" size="sm" onClick={() => openEditSpec(s)}>
                        Edit
                      </Button>
                      <Button type="button" variant="danger" size="sm" onClick={() => setDeleteTarget(s)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Modal
        isOpen={specEditorOpen}
        onClose={() => {
          if (!savingSpec) setSpecEditorOpen(false);
        }}
        title={specEditingId ? 'Edit factory spec' : 'Add factory spec'}
        description="Year, make, model, and trim identify the catalog entry. Saving an existing combination updates it."
        size="lg"
      >
        <form onSubmit={saveSpec} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(
              [
                ['year', 'Year', 'number'],
                ['make', 'Make', 'text'],
                ['model', 'Model', 'text'],
                ['trim', 'Trim (optional)', 'text'],
                ['power_hp', 'Power (hp)', 'number'],
                ['torque_lbft', 'Torque (lb-ft)', 'number'],
                ['weight_lbs', 'Weight (lbs)', 'number'],
                ['engine', 'Engine', 'text'],
                ['transmission', 'Transmission', 'text'],
                ['drivetrain', 'Drivetrain', 'text'],
                ['forced_induction', 'Forced induction', 'text'],
                ['source', 'Source (optional)', 'text'],
              ] as const
            ).map(([key, label, type]) => (
              <div key={key}>
                <label htmlFor={`spec-${key}`} className="input-label">
                  {label}
                </label>
                <input
                  id={`spec-${key}`}
                  type={type}
                  value={specForm[key]}
                  onChange={(e) => setSpecField(key, e.target.value)}
                  className="input"
                  placeholder={
                    key === 'drivetrain' ? 'RWD, AWD, FWD' : key === 'forced_induction' ? 'Turbo, supercharger, or none' : undefined
                  }
                />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={specForm.is_estimated}
              onChange={(e) => setSpecField('is_estimated', e.target.checked)}
              className="w-4 h-4"
            />
            Values are estimated
          </label>
          {specFormError ? (
            <p role="alert" className="text-red-300 text-sm">
              {specFormError}
            </p>
          ) : null}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="ghost" disabled={savingSpec} onClick={() => setSpecEditorOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={savingSpec} loadingLabel="Saving…">
              {specEditingId ? 'Save changes' : 'Create spec'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete factory spec"
        description={
          deleteTarget
            ? `Delete the factory catalog entry for ${deleteTarget.year} ${deleteTarget.make} ${deleteTarget.model}${
                deleteTarget.trim ? ` ${deleteTarget.trim}` : ''
              }? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        loading={deleting}
        loadingLabel="Working…"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AdminFactorySpecs;
